import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { comparePlayers, getPlayers } from '@/lib/api'
import { PageMotion } from '@/components/Motion'
import {
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  PageHeader,
  Panel,
} from '@/components/ui'
import { cn, formatKD, formatNum, formatPct } from '@/lib/utils'

export default function Compare() {
  const [q1, setQ1] = useState('')
  const [q2, setQ2] = useState('')
  const [id1, setId1] = useState<number | null>(null)
  const [id2, setId2] = useState<number | null>(null)

  const search1 = useQuery({
    queryKey: ['player-search', q1],
    queryFn: () => getPlayers(1, 8, q1),
    enabled: q1.length >= 2 && !id1,
  })
  const search2 = useQuery({
    queryKey: ['player-search', q2],
    queryFn: () => getPlayers(1, 8, q2),
    enabled: q2.length >= 2 && !id2,
  })

  const compare = useQuery({
    queryKey: ['compare', id1, id2],
    queryFn: () => comparePlayers(id1!, id2!),
    enabled: !!id1 && !!id2,
  })

  const rows = useMemo(() => {
    if (!compare.data) return []
    const a = compare.data.player1
    const b = compare.data.player2
    return [
      { label: 'Missions', a: a.missionsPlayed, b: b.missionsPlayed, higher: true },
      { label: 'Kills', a: a.totalKills, b: b.totalKills, higher: true },
      { label: 'Deaths', a: a.totalDeaths, b: b.totalDeaths, higher: false },
      { label: 'K/D', a: a.kd, b: b.kd, higher: true, fmt: formatKD },
      { label: 'Shots', a: a.totalShots, b: b.totalShots, higher: true },
      { label: 'Hits', a: a.totalHits, b: b.totalHits, higher: true },
      { label: 'Accuracy', a: a.accuracy, b: b.accuracy, higher: true, fmt: formatPct },
    ]
  }, [compare.data])

  return (
    <PageMotion className="space-y-8">
      <PageHeader title="Compare" subtitle="Side-by-side career stats" />

      <div className="grid gap-4 sm:grid-cols-2">
        <PlayerPicker
          label="Player A"
          query={q1}
          setQuery={(v) => {
            setQ1(v)
            setId1(null)
          }}
          selectedId={id1}
          results={search1.data?.data ?? []}
          onSelect={(id, name) => {
            setId1(id)
            setQ1(name)
          }}
        />
        <PlayerPicker
          label="Player B"
          query={q2}
          setQuery={(v) => {
            setQ2(v)
            setId2(null)
          }}
          selectedId={id2}
          results={search2.data?.data ?? []}
          onSelect={(id, name) => {
            setId2(id)
            setQ2(name)
          }}
        />
      </div>

      {!id1 || !id2 ? (
        <EmptyState message="Select two players to compare" />
      ) : compare.isLoading ? (
        <LoadingBlock />
      ) : compare.error ? (
        <ErrorBlock message="Compare failed" />
      ) : (
        <Panel>
          <div className="grid grid-cols-3 gap-2 text-center text-sm font-medium mb-4 pb-3 border-b border-line">
            <div className="text-accent-text truncate">{compare.data?.player1.name}</div>
            <div className="text-ink-faint text-xs uppercase tracking-[0.14em] self-center">
              vs
            </div>
            <div className="text-sky-300 truncate">{compare.data?.player2.name}</div>
          </div>
          <ul className="space-y-1">
            {rows.map((r) => {
              const fmt = r.fmt ?? ((n: number) => formatNum(n))
              const aWins = r.higher ? r.a > r.b : r.a < r.b
              const bWins = r.higher ? r.b > r.a : r.b < r.a
              return (
                <li
                  key={r.label}
                  className="grid grid-cols-3 gap-2 items-center text-sm py-2.5 border-b border-line-soft last:border-0"
                >
                  <div
                    className={cn(
                      'font-mono text-right tabular-nums',
                      aWins && r.a !== r.b ? 'text-accent-text' : 'text-ink',
                    )}
                  >
                    {fmt(r.a as number)}
                  </div>
                  <div className="text-center text-xs text-ink-faint uppercase tracking-[0.14em]">
                    {r.label}
                  </div>
                  <div
                    className={cn(
                      'font-mono text-left tabular-nums',
                      bWins && r.a !== r.b ? 'text-sky-300' : 'text-ink',
                    )}
                  >
                    {fmt(r.b as number)}
                  </div>
                </li>
              )
            })}
          </ul>
        </Panel>
      )}
    </PageMotion>
  )
}

function PlayerPicker({
  label,
  query,
  setQuery,
  selectedId,
  results,
  onSelect,
}: {
  label: string
  query: string
  setQuery: (v: string) => void
  selectedId: number | null
  results: { id: number; name: string; kills: number }[]
  onSelect: (id: number, name: string) => void
}) {
  return (
    <div className="panel p-4">
      <label className="label">{label}</label>
      <input
        className="input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Type a name…"
      />
      {selectedId ? (
        <p className="mt-2 text-xs text-accent-text">Selected #{selectedId}</p>
      ) : results.length > 0 ? (
        <ul className="mt-2 rounded-panel border border-line max-h-40 overflow-auto">
          {results.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent-soft flex justify-between transition-colors"
                onClick={() => onSelect(p.id, p.name)}
              >
                <span className="text-ink">{p.name}</span>
                <span className="font-mono text-xs text-ink-faint">{p.kills}k</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
